import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import InfoIcon from '@mui/icons-material/Info';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import { useNavigate } from 'react-router-dom';
import {
  getTiebreakTypes,
  getTournamentTiebreaks,
  calculateTeamsStandings,
  saveTeamsStandings,
} from '../../../services/tournament.service';
import {
  TIEBREAK_TIPO_TORNEO,
  TOURNAMENT_TIPO_PUNTAJE,
} from '../../../constants/tournament.constants';

/**
 * Paso 4 del wizard para torneos por equipos.
 * Selección de desempates, cálculo de standings y guardado final.
 *
 * Props:
 *   torId        - ID del torneo
 *   tipoPuntaje  - 1=Match Points, 2=Puntos individuales
 *   onBack       - Callback para volver al paso anterior
 *   setAlertInfo - Callback para mostrar alertas
 */
export default function StepTeamStandings({ torId, tipoPuntaje = 1, onBack, setAlertInfo }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [dbTiebreaks, setDbTiebreaks] = useState([]);
  const [selectedTiebreaks, setSelectedTiebreaks] = useState([]);
  const [standings, setStandings] = useState([]);

  const isMatchPoints = tipoPuntaje === TOURNAMENT_TIPO_PUNTAJE.MATCH_POINTS;
  const primaryScoreLabel = isMatchPoints ? 'Match Points' : 'Pts Individuales';
  const tipoPuntajeLabel = isMatchPoints
    ? 'Match Points (2/1/0)'
    : 'Puntos Individuales (tableros)';

  // Inicialización: carga desempates disponibles, configuración guardada y standings actuales
  useEffect(() => {
    const initializeStep = async () => {
      if (!torId) return;
      setStandingsLoading(true);
      try {
        // 1. Desempates disponibles para torneos por equipos
        const types = await getTiebreakTypes(TIEBREAK_TIPO_TORNEO.EQUIPOS);
        setDbTiebreaks(types || []);

        // 2. Configuración de desempates guardada para este torneo
        const savedConfigs = await getTournamentTiebreaks(torId);
        const savedNames = savedConfigs
          .sort((a, b) => a.tpt_orden - b.tpt_orden)
          .map((c) => c.ttb_nombre);
        setSelectedTiebreaks(savedNames);

        // 3. Calcular standings iniciales
        const initialStandings = await calculateTeamsStandings(torId, savedNames);
        setStandings(initialStandings || []);
      } catch (error) {
        console.error('Error initializing team standings step:', error);
        setAlertInfo({
          open: true,
          message: 'Error al inicializar la clasificación de equipos.',
          severity: 'error',
        });
      } finally {
        setStandingsLoading(false);
      }
    };

    initializeStep();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [torId]);

  // Recalcular con los desempates seleccionados
  const handleApplyTiebreaks = async () => {
    if (!torId) return;
    setStandingsLoading(true);
    try {
      const updatedStandings = await calculateTeamsStandings(torId, selectedTiebreaks);
      setStandings(updatedStandings || []);
      setAlertInfo({
        open: true,
        message: 'Desempates aplicados correctamente.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error calculating team standings:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al calcular la clasificación.',
        severity: 'error',
      });
    } finally {
      setStandingsLoading(false);
    }
  };

  const handleAddTiebreak = (name) => {
    if (selectedTiebreaks.includes(name)) return;
    setSelectedTiebreaks([...selectedTiebreaks, name]);
  };

  const handleRemoveTiebreak = (name) => {
    setSelectedTiebreaks(selectedTiebreaks.filter((t) => t !== name));
  };

  const handleMoveTiebreakUp = (index) => {
    if (index === 0) return;
    const updated = [...selectedTiebreaks];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    setSelectedTiebreaks(updated);
  };

  const handleMoveTiebreakDown = (index) => {
    if (index === selectedTiebreaks.length - 1) return;
    const updated = [...selectedTiebreaks];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setSelectedTiebreaks(updated);
  };

  // Reordenamiento manual de equipos en la tabla
  const handleMoveTeamUp = (index) => {
    if (index === 0) return;
    const updated = [...standings];
    [updated[index], updated[index - 1]] = [updated[index - 1], updated[index]];
    setStandings(updated.map((t, idx) => ({ ...t, posicionFinal: idx + 1 })));
  };

  const handleMoveTeamDown = (index) => {
    if (index === standings.length - 1) return;
    const updated = [...standings];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    setStandings(updated.map((t, idx) => ({ ...t, posicionFinal: idx + 1 })));
  };

  // Guardar posiciones y desempates
  const handleSaveAndFinish = async () => {
    if (!torId) return;
    setLoading(true);
    try {
      const positions = standings.map((team) => ({
        equipoId: team.equipoId,
        posicionFinal: team.posicionFinal,
        puntajeFinal: team.puntos,
      }));

      await saveTeamsStandings(torId, {
        positions,
        tiebreaks: selectedTiebreaks,
      });

      setAlertInfo({
        open: true,
        message: 'Clasificación y criterios de desempate guardados correctamente.',
        severity: 'success',
      });

      setTimeout(() => {
        navigate('/tournaments');
      }, 1500);
    } catch (error) {
      console.error('Error saving team standings:', error);
      setAlertInfo({
        open: true,
        message: error.message || 'Error al guardar la clasificación final.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Color de podio
  const getPodiumColor = (idx) => {
    if (idx === 0) return '#fbbf24';
    if (idx === 1) return '#cbd5e1';
    if (idx === 2) return '#d97706';
    return '#94a3b8';
  };

  return (
    <Box>
      {/* Encabezado */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" sx={{ color: '#cbd5e1', fontWeight: 600, flexGrow: 1 }}>
          Paso 4: Cálculo de Puntos y Posiciones Finales
        </Typography>
        <Chip
          icon={<EmojiEventsIcon sx={{ fontSize: '14px !important' }} />}
          label={`Modalidad: ${tipoPuntajeLabel}`}
          size="small"
          sx={{
            borderRadius: 0,
            backgroundColor: isMatchPoints
              ? 'rgba(58, 110, 182, 0.15)'
              : 'rgba(16, 185, 129, 0.15)',
            color: isMatchPoints ? '#93c5fd' : '#6ee7b7',
            border: '1px solid',
            borderColor: isMatchPoints
              ? 'rgba(58, 110, 182, 0.3)'
              : 'rgba(16, 185, 129, 0.3)',
            fontSize: '11px',
          }}
        />
      </Box>

      {/* Panel superior: Selección de criterios */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          background: 'rgba(15, 23, 42, 0.5)',
          borderColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Grid container spacing={3}>
          {/* Izquierda: Criterios disponibles */}
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Criterios Disponibles
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Haz clic en un desempate para añadirlo al orden de aplicación.
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {dbTiebreaks.map((tb) => {
                  const isSelected = selectedTiebreaks.includes(tb.ttb_nombre);
                  return (
                    <Chip
                      key={tb.ttb_id}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {tb.ttb_nombre}
                          <Tooltip title={tb.ttb_descripcion || 'Sin descripción'} arrow placement="top">
                            <InfoIcon sx={{ fontSize: '14px', color: '#64748b', cursor: 'pointer', '&:hover': { color: '#cbd5e1' } }} />
                          </Tooltip>
                        </Box>
                      }
                      onClick={isSelected ? undefined : () => handleAddTiebreak(tb.ttb_nombre)}
                      disabled={isSelected}
                      icon={<AddIcon fontSize="small" />}
                      clickable={!isSelected}
                      sx={{
                        borderRadius: 0,
                        backgroundColor: isSelected ? 'rgba(51, 65, 85, 0.3)' : 'rgba(58, 110, 182, 0.1)',
                        color: isSelected ? '#475569' : '#cbd5e1',
                        border: '1px solid',
                        borderColor: isSelected ? 'transparent' : 'rgba(58, 110, 182, 0.3)',
                        '&:hover': isSelected ? {} : {
                          backgroundColor: 'rgba(58, 110, 182, 0.2)',
                          borderColor: '#3a6eb6',
                        },
                        '& .MuiChip-icon': {
                          color: isSelected ? '#475569' : '#3a6eb6',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          </Grid>

          {/* Derecha: Criterios seleccionados */}
          <Grid item xs={12} md={6} sx={{ borderLeft: { md: '1px solid rgba(255, 255, 255, 0.08)' }, pl: { md: 3 } }}>
            <Box>
              <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600, mb: 1 }}>
                Orden de Criterios Aplicados
              </Typography>
              <Typography variant="body2" sx={{ color: '#94a3b8', mb: 2 }}>
                Define la prioridad de desempate de arriba hacia abajo usando las flechas.
              </Typography>

              {selectedTiebreaks.length === 0 ? (
                <Box
                  sx={{
                    p: 2,
                    textAlign: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                  }}
                >
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    Ningún desempate seleccionado. Se ordenará únicamente por puntaje primario.
                  </Typography>
                </Box>
              ) : (
                <List dense sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
                  {selectedTiebreaks.map((name, index) => {
                    const fullInfo = dbTiebreaks.find((d) => d.ttb_nombre === name);
                    return (
                      <ListItem
                        key={name}
                        sx={{
                          mb: 1,
                          backgroundColor: 'rgba(15, 23, 42, 0.3)',
                          border: '1px solid rgba(255, 255, 255, 0.05)',
                          py: 0.5,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 35 }}>
                          <Chip
                            label={`${index + 1}°`}
                            size="small"
                            sx={{
                              borderRadius: 0,
                              backgroundColor: '#3a6eb6',
                              color: '#ffffff',
                              fontWeight: 'bold',
                              height: '20px',
                              fontSize: '11px',
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={name}
                          primaryTypographyProps={{ style: { color: '#cbd5e1', fontWeight: 500, fontSize: '14px' } }}
                          secondary={fullInfo ? fullInfo.ttb_descripcion : ''}
                          secondaryTypographyProps={{
                            style: { color: '#64748b', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '200px' },
                          }}
                        />
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => handleMoveTiebreakUp(index)}
                            sx={{ color: '#94a3b8', '&:disabled': { color: '#334155' } }}
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            disabled={index === selectedTiebreaks.length - 1}
                            onClick={() => handleMoveTiebreakDown(index)}
                            sx={{ color: '#94a3b8', '&:disabled': { color: '#334155' } }}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveTiebreak(name)}
                            sx={{ color: '#ef4444', '&:hover': { background: 'rgba(239, 68, 68, 0.1)' } }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ListItem>
                    );
                  })}
                </List>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={<PlayArrowIcon />}
                onClick={handleApplyTiebreaks}
                disabled={standingsLoading}
                sx={{
                  mt: 2,
                  background: '#10b981',
                  borderRadius: 0,
                  '&:hover': { background: '#059669' },
                  '&:disabled': { background: 'rgba(16, 185, 129, 0.3)' },
                }}
              >
                {standingsLoading ? <CircularProgress size={20} color="inherit" /> : 'Aplicar Desempates'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Panel inferior: Tabla de clasificación */}
      <Paper
        variant="outlined"
        sx={{
          p: 3,
          background: 'rgba(15, 23, 42, 0.5)',
          borderColor: 'rgba(255, 255, 255, 0.08)',
          borderRadius: 0,
          minHeight: '320px',
          display: 'flex',
          flexDirection: 'column',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ color: '#cbd5e1', fontWeight: 600 }}>
            Clasificación General — Equipos
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            * Usa las flechas en "Ajuste Manual" para forzar el puesto de un equipo.
          </Typography>
        </Box>

        {standingsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : standings.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, py: 4 }}>
            <Typography variant="body2" sx={{ color: '#64748b' }}>
              No hay datos de clasificación disponibles. Asegúrate de tener equipos y rondas cargadas.
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.05)' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '60px' }}>
                    Puesto
                  </TableCell>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                    Equipo
                  </TableCell>
                  <TableCell sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '110px' }}>
                    {primaryScoreLabel}
                  </TableCell>
                  {/* Columnas de desempates */}
                  {selectedTiebreaks.map((tbName) => (
                    <TableCell
                      key={tbName}
                      sx={{
                        py: 0.5,
                        background: '#0f172a',
                        color: '#3a6eb6',
                        fontWeight: 'bold',
                        borderColor: 'rgba(255, 255, 255, 0.08)',
                        fontSize: '11px',
                        width: '90px',
                        textAlign: 'center',
                      }}
                    >
                      {tbName}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ py: 0.5, background: '#0f172a', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)', width: '100px' }}>
                    Ajuste Manual
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standings.map((team, idx) => (
                  <TableRow key={team.equipoId} hover sx={{ '&:hover': { background: 'rgba(255, 255, 255, 0.02) !important' } }}>
                    <TableCell sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)', fontWeight: 'bold', color: getPodiumColor(idx) }}>
                      {team.posicionFinal ?? (idx + 1)}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, color: '#cbd5e1', fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      {team.nombre || `Equipo #${team.equipoId}`}
                    </TableCell>
                    <TableCell sx={{ py: 0.5, color: '#10b981', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      {team.puntos}
                    </TableCell>
                    {/* Valores de cada desempate */}
                    {selectedTiebreaks.map((tbName) => (
                      <TableCell
                        key={tbName}
                        sx={{
                          py: 0.5,
                          color: '#94a3b8',
                          borderColor: 'rgba(255, 255, 255, 0.05)',
                          fontSize: '13px',
                          width: '90px',
                          textAlign: 'center',
                        }}
                      >
                        {team.tiebreaks?.[tbName] !== undefined
                          ? Number(team.tiebreaks[tbName]).toFixed(1)
                          : '0.0'}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{ py: 0.5, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                      <IconButton
                        size="small"
                        disabled={idx === 0}
                        onClick={() => handleMoveTeamUp(idx)}
                        sx={{ p: 0.25, color: '#3a6eb6', '&:disabled': { color: '#334155' } }}
                      >
                        <KeyboardArrowUpIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        disabled={idx === standings.length - 1}
                        onClick={() => handleMoveTeamDown(idx)}
                        sx={{ p: 0.25, color: '#3a6eb6', '&:disabled': { color: '#334155' } }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Acciones del paso */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          disabled={loading}
          sx={{
            color: '#94a3b8',
            borderColor: 'rgba(255, 255, 255, 0.2)',
            borderRadius: 0,
            '&:hover': { borderColor: '#cbd5e1', background: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          Atrás
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSaveAndFinish}
          disabled={loading || standings.length === 0}
          sx={{
            background: '#3a6eb6',
            borderRadius: 0,
            '&:hover': { background: '#2a5793' },
            '&:disabled': { background: 'rgba(58, 110, 182, 0.3)' },
          }}
        >
          {loading ? <CircularProgress size={20} color="inherit" /> : 'Guardar y Finalizar'}
        </Button>
      </Box>
    </Box>
  );
}
